import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/shared/Layout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AdminDashboard from "@/pages/AdminDashboard";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import AdvertiserDashboard from "@/pages/AdvertiserDashboard";
import OfferMarketplace from "@/pages/OfferMarketplace";
import TrafficAnalytics from "@/pages/TrafficAnalytics";
import PayoutManagement from "@/pages/PayoutManagement";
import AIInsights from "@/pages/AIInsights";
import SubAffiliates from "@/pages/SubAffiliates";
import FraudDetection from "@/pages/FraudDetection";
import ConversionPostback from "@/pages/ConversionPostback";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelection from "@/pages/RoleSelection";
import ReportCenter from "@/pages/ReportCenter";
import WhiteLabelSettings from "@/pages/WhiteLabelSettings";
import CurrencySettings from "@/pages/CurrencySettings";
import PayoutScheduling from "@/pages/PayoutScheduling";
import BulkImport from "@/pages/BulkImport";
import DomainMapping from "@/pages/DomainMapping";
import WebhookManagement from "@/pages/WebhookManagement";
import ABTesting from "@/pages/ABTesting";
import SmartLinksPage from "@/pages/SmartLinksPage";
import AuditLogPage from "@/pages/AuditLogPage";
import AccountManagement from "@/pages/AccountManagement";
import LandingPage from "@/pages/LandingPage";
import CreatorStudio from "@/pages/CreatorStudio";
import CollectionDetail from "@/pages/CollectionDetail";
import PublicStorefront from "@/pages/PublicStorefront";
import PublicShop from "@/pages/PublicShop";
import CreatorDiscovery from "@/pages/CreatorDiscovery";
import AmazonAssociates from "@/pages/AmazonAssociates";
import VideoLibrary from "@/pages/VideoLibrary";
import YouTubeEmbeds from "@/pages/YouTubeEmbeds";
import LiveStreaming from "@/pages/LiveStreaming";
import SponsoredPlacements from "@/pages/SponsoredPlacements";
import ComplianceCenter from "@/pages/ComplianceCenter";
import GlobalSettings from "@/pages/GlobalSettings";
import DocumentationHub from "@/pages/DocumentationHub";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3 animate-pulse">
          <span className="text-white font-bold text-lg">DW</span>
        </div>
        <p className="text-zinc-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  return <Layout>{children}</Layout>;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin" />;
  if (user?.role === 'advertiser') return <Navigate to="/advertiser" />;
  return <Navigate to="/affiliate" />;
}

function AppRoutes() {
  const loc = window.location;
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id from Google OAuth callback synchronously
  if (loc.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/select-role" element={<RoleSelection />} />
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><RoleRedirect /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>} />
      <Route path="/affiliate/links" element={<ProtectedRoute><SmartLinksPage /></ProtectedRoute>} />
      <Route path="/advertiser" element={<ProtectedRoute><AdvertiserDashboard /></ProtectedRoute>} />
      <Route path="/advertiser/offers" element={<ProtectedRoute><AdvertiserDashboard tab="offers" /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><OfferMarketplace /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><TrafficAnalytics /></ProtectedRoute>} />
      <Route path="/payouts" element={<ProtectedRoute><PayoutManagement /></ProtectedRoute>} />
      <Route path="/ai-insights" element={<ProtectedRoute><AIInsights /></ProtectedRoute>} />
      <Route path="/sub-affiliates" element={<ProtectedRoute><SubAffiliates /></ProtectedRoute>} />
      <Route path="/fraud" element={<ProtectedRoute><FraudDetection /></ProtectedRoute>} />
      <Route path="/postbacks" element={<ProtectedRoute><ConversionPostback /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportCenter /></ProtectedRoute>} />
      <Route path="/settings/whitelabel" element={<ProtectedRoute><WhiteLabelSettings /></ProtectedRoute>} />
      <Route path="/settings/currency" element={<ProtectedRoute><CurrencySettings /></ProtectedRoute>} />
      <Route path="/scheduling" element={<ProtectedRoute><PayoutScheduling /></ProtectedRoute>} />
      <Route path="/bulk-import" element={<ProtectedRoute><BulkImport /></ProtectedRoute>} />
      <Route path="/domains" element={<ProtectedRoute><DomainMapping /></ProtectedRoute>} />
      <Route path="/webhooks" element={<ProtectedRoute><WebhookManagement /></ProtectedRoute>} />
      <Route path="/ab-testing" element={<ProtectedRoute><ABTesting /></ProtectedRoute>} />
      <Route path="/audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
      <Route path="/accounts" element={<AdminRoute><AccountManagement /></AdminRoute>} />
      <Route path="/creator-studio" element={<ProtectedRoute><CreatorStudio /></ProtectedRoute>} />
      <Route path="/creator-studio/collections/:collectionId" element={<ProtectedRoute><CollectionDetail /></ProtectedRoute>} />
      <Route path="/discover" element={<ProtectedRoute><CreatorDiscovery /></ProtectedRoute>} />
      <Route path="/amazon" element={<ProtectedRoute><AmazonAssociates /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute><VideoLibrary /></ProtectedRoute>} />
      <Route path="/youtube" element={<ProtectedRoute><YouTubeEmbeds /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><LiveStreaming /></ProtectedRoute>} />
      <Route path="/sponsored" element={<ProtectedRoute><SponsoredPlacements /></ProtectedRoute>} />
      <Route path="/compliance" element={<AdminRoute><ComplianceCenter /></AdminRoute>} />
      <Route path="/global-settings" element={<ProtectedRoute><GlobalSettings /></ProtectedRoute>} />
      <Route path="/docs" element={<ProtectedRoute><DocumentationHub /></ProtectedRoute>} />
      <Route path="/storefront/:slug" element={<PublicStorefront />} />
      <Route path="/shop" element={<PublicShop />} />
      <Route path="/shop/:creatorSlug" element={<PublicShop />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" theme="dark" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
